import SiteLayout from '@/components/feature/SiteLayout';
import PageIntro from '@/components/feature/PageIntro';
import BookingForm from '@/components/feature/BookingForm';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { brandInfo, contactIntroImage } from '@/mocks/site';

export default function ContactPage() {
  useDocumentMeta('Contact & Book a Service | Redline Garage', 'Request a service appointment at Redline Garage. Share your vehicle details and preferred date, and our team will call to confirm your booking.');

  return (
    <SiteLayout>
      <PageIntro
        title="Contact & Booking"
        text="Request a service appointment or get in touch — we'll confirm your booking by phone."
        image={contactIntroImage}
        alt="Auto repair workshop exterior at dusk"
      />
      <section className="py-16 md:py-[104px] bg-background-50">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-x-[72px]">
          <div className="lg:col-span-5 flex flex-col gap-10">
            <div className="border-t border-background-300/70 pt-6">
              <h2 className="font-heading uppercase text-[24px]">Visit the workshop</h2>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex gap-3"><span className="w-5 h-5 flex items-center justify-center text-primary-400"><i className="ri-map-pin-line" /></span><span className="text-foreground-800">{brandInfo.address}<br />{brandInfo.city}</span></li>
                <li className="flex gap-3"><span className="w-5 h-5 flex items-center justify-center text-primary-400"><i className="ri-phone-line" /></span><a href={brandInfo.phoneHref} className="text-foreground-800 hover:text-primary-400 cursor-pointer">{brandInfo.phone}</a></li>
                <li className="flex gap-3"><span className="w-5 h-5 flex items-center justify-center text-primary-400"><i className="ri-mail-line" /></span><a href={`mailto:${brandInfo.email}`} className="text-foreground-800 hover:text-primary-400 cursor-pointer break-all">{brandInfo.email}</a></li>
              </ul>
            </div>
            <div className="border-t border-background-300/70 pt-6">
              <h2 className="font-heading uppercase text-[24px]">Opening hours</h2>
              <ul className="mt-5 text-sm">
                {brandInfo.hours.map((h) => (
                  <li key={h.day} className="flex justify-between gap-4 py-3 border-b border-background-300/50 last:border-0">
                    <span className="text-foreground-700">{h.day}</span>
                    <span className={h.time === 'Closed' ? 'text-primary-400' : 'text-foreground-900'}>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative h-[240px] lg:flex-1 lg:min-h-[220px] overflow-hidden border border-background-300/60 bg-background-100 flex flex-col items-center justify-center text-center px-6" role="img" aria-label="Workshop location map placeholder">
              <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(oklch(var(--background-300))_1px,transparent_1px),linear-gradient(90deg,oklch(var(--background-300))_1px,transparent_1px)] [background-size:32px_32px]" aria-hidden="true" />
              <span className="relative w-10 h-10 flex items-center justify-center text-primary-500 text-2xl"><i className="ri-map-pin-2-line" /></span>
              <p className="relative mt-3 text-[11px] uppercase tracking-[0.18em] text-foreground-700 font-label">Workshop location</p>
              <p className="relative mt-1 text-sm text-foreground-900">{brandInfo.address}, {brandInfo.city}</p>
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col">
            <h2 className="font-heading uppercase text-[36px] md:text-[48px] leading-[0.95] mb-8">Request <span className="text-primary-500">service.</span></h2>
            <BookingForm variant="linear" />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}